import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { format, isValid } from 'date-fns';
import {
  CLEPeriod,
  CreditTrackerUpdateRequest,
  ICLEPeriod,
  IComplianceGroup,
  ReportingPeriodType,
  UserTrackerData,
  UserTrackerDataRequest
} from '@/@types/client-api';
import { ModalProps } from '@/components/ui/Modal/Modal';
import { findInRecordCollection } from '@/utils/recordHelpers';
import {
  useUpdateComplianceDateRangeMutation,
  useUpdateUserCleOptionsMutation,
  useSwitchStatusLazyMutation,
  useRefetchCreditTrackerMutation
} from '@/redux/api/client/creditTracker';
import { selectActiveUserTrackerData } from '@/redux/selectors/creditTrackerSelectors';
import useToast from '@/hooks/useToast';
import {
  ComplianceFieldNames,
  SpecialStatusSelector
} from '@/components/ui/CreditTracker/util/constants';
import {
  findSelectedClePeriod,
  getActiveCompliancePeriodValue,
  getSpecialStatuses,
  getTransitionOptions,
  periodIsMissingComplianceInformation
} from '../util/TrackerHelpers';
import { ComplianceFormFields } from '@/components/ui/CreditTracker/Forms/ComplianceForm';
import useComplianceFormValidation from '@/components/ui/CreditTracker/hooks/validation/useComplianceFormValidationHook';
import {
  compareDates,
  getPeriodAdmitDate,
  getPeriodEndDate,
  getPeriodStartDate,
  getUserBirthDate,
  isUsingDefaultDates
} from '@/components/ui/CreditTracker/util/trackerDateHelpers';
import usePrevious from '@/hooks/usePrevious';
import { CreditTrackerContext } from '@/components/blocks/CreditTracker/CreditTrackerContext';
import { compareObjects } from '@/utils/helpers';
import {
  excludeNowDateFlags,
  getNowDateFlagsFromRequest
} from '@/components/ui/CreditTracker/util/nowDateFlag';

export interface UseComplianceModalProps extends ModalProps {
  customerSk: number;
  isMissingCompliancePeriod?: boolean;
  complianceFormFields?: ComplianceFormFields;
  /**
   * Override for Redux license
   */
  trackerLicense?: UserTrackerData;
  birthDate?: Date;
  lastTrackerRequest?: UserTrackerDataRequest;
  setIsLoading?: (loading: boolean) => void;
  initialSpecialStatuses?: SpecialStatusSelector[];
  reportingPeriodOverride?: ReportingPeriodType;
  commitSpecialStatusOnFormSubmit?: boolean;
  onSpecialStatusChange?: (
    statuses: SpecialStatusSelector[]
  ) => ComplianceFormFields | undefined | Promise<ComplianceFormFields | undefined>;
}

const useComplianceModal = ({
  customerSk,
  isMissingCompliancePeriod: isMissingCompliancePeriodOverride,
  complianceFormFields,
  trackerLicense,
  lastTrackerRequest: lastTrackerRequestOverride,
  setIsLoading: setIsLoadingOverride,
  initialSpecialStatuses,
  reportingPeriodOverride,
  commitSpecialStatusOnFormSubmit,
  onSpecialStatusChange,
  open,
  setOpen
}: UseComplianceModalProps) => {
  const {
    isMissingCompliancePeriod: contextIsMissingCompliancePeriod,
    lastTrackerRequest: contextLastTrackerRequest
  } = useContext(CreditTrackerContext);

  const isMissingCompliancePeriod =
    isMissingCompliancePeriodOverride ?? contextIsMissingCompliancePeriod;
  const lastTrackerRequest = lastTrackerRequestOverride ?? contextLastTrackerRequest;
  const setIsLoading = setIsLoadingOverride;
  const lastTrackerRequestNowDateFlags = useMemo(
    () => getNowDateFlagsFromRequest(lastTrackerRequest?.flags),
    [lastTrackerRequest?.flags]
  );

  const activelyViewingUserSk = useMemo(
    () => customerSk ?? (lastTrackerRequest?.individualSk ? lastTrackerRequest.individualSk : -1),
    [customerSk, lastTrackerRequest?.individualSk]
  );

  const selectedRegionSelector = useMemo(
    () => selectActiveUserTrackerData(activelyViewingUserSk),
    [activelyViewingUserSk]
  );
  const _selectedRegion = useSelector(selectedRegionSelector);
  const [updateUserOptions] = useUpdateUserCleOptionsMutation();
  const [updateComplianceDateRange] = useUpdateComplianceDateRangeMutation();
  const [getComplianceStartEndDate] = useRefetchCreditTrackerMutation();
  const [switchStatusLazy] = useSwitchStatusLazyMutation();
  const { showSuccessMessage, showFailMessage } = useToast();
  const [isSubmitting, setIsSubmitting] = useState<boolean>();
  const [isUpdatingDateRange, setIsUpdatingDateRange] = useState<boolean>();
  const [isUpdatingForm, setIsUpdatingForm] = useState<boolean>();
  const [selectedRegion, setSelectedRegion] = useState(
    () => trackerLicense ?? _selectedRegion ?? {}
  );

  useEffect(() => {
    const nextSelectedRegion = trackerLicense ?? _selectedRegion ?? {};

    setSelectedRegion(prev => {
      return compareObjects(prev, nextSelectedRegion) ? prev : nextSelectedRegion;
    });
  }, [_selectedRegion, trackerLicense]);

  const transitionOptions = useMemo(
    () => getTransitionOptions(selectedRegion ?? {}),
    [selectedRegion]
  );

  const allSpecialStatuses = useMemo(
    () => getSpecialStatuses(transitionOptions ?? []),
    [transitionOptions]
  );

  const [statuses, setStatuses] = useState<SpecialStatusSelector[]>(
    initialSpecialStatuses ?? allSpecialStatuses ?? []
  );
  const [pendingStatuses, setPendingStatuses] = useState<SpecialStatusSelector[]>();
  const [showSpecialStatusError, setShowSpecialStatusError] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    setShowSpecialStatusError(false);
    setPendingStatuses(undefined);
  }, [open]);

  useEffect(() => {
    if (commitSpecialStatusOnFormSubmit || initialSpecialStatuses) return;

    const nextStatuses = allSpecialStatuses ?? [];
    setStatuses(prev => {
      return JSON.stringify(prev) === JSON.stringify(nextStatuses) ? prev : nextStatuses;
    });
  }, [allSpecialStatuses, commitSpecialStatusOnFormSubmit, initialSpecialStatuses]);

  const emptyComplianceFormFields: ComplianceFormFields = useMemo(
    () =>
      ({
        complianceGroup: null,
        birthDay: undefined,
        birthMonth: undefined,
        admitYear: undefined,
        admitDate: undefined,
        startDate: '',
        endDate: '',
        specialStatus: null
      }) as ComplianceFormFields,
    []
  );

  const selectedClePeriod = useMemo(
    () => findSelectedClePeriod(selectedRegion?.jurisdiction?.clePeriod),
    [selectedRegion?.jurisdiction?.clePeriod]
  );

  const [compliancePeriod, setCompliancePeriod] =
    useState<ComplianceFormFields>(emptyComplianceFormFields);

  useEffect(() => {
    if (!open) {
      setCompliancePeriod(complianceFormFields ?? emptyComplianceFormFields);
    }
  }, [complianceFormFields, emptyComplianceFormFields, open]);

  const reportingPeriod = useMemo(
    () =>
      compliancePeriod.reportingPeriod ??
      reportingPeriodOverride ??
      selectedRegion?.jurisdiction?.jurisdictionReportingPeriod?.reportingPeriodType,
    [
      compliancePeriod.reportingPeriod,
      reportingPeriodOverride,
      selectedRegion?.jurisdiction?.jurisdictionReportingPeriod?.reportingPeriodType
    ]
  );

  const initialMount = useRef(true);
  const specialStatusChangeRequestRef = useRef(0);
  const dateRangeRequestRef = useRef(0);
  const pendingDateRangeUpdatesRef = useRef(new Set<Promise<UserTrackerData | undefined>>());
  const hasProvisionalPeriodUpdateRef = useRef(false);
  const isResettingRef = useRef(false);

  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      if (complianceFormFields) {
        setCompliancePeriod(complianceFormFields);
      }
    }
  }, [complianceFormFields]);

  const selectedStatuses = useMemo(() => pendingStatuses ?? statuses, [pendingStatuses, statuses]);

  const specialStatusChanged = useMemo(() => {
    return (
      allSpecialStatuses?.find(a => a.value?.toLowerCase() === 'true')?.text !=
      selectedStatuses?.find(a => a.value?.toLowerCase() === 'true')?.text
    );
  }, [allSpecialStatuses, selectedStatuses]);

  const isTransitionalOrNewlyAdmittedRegion = useMemo(() => {
    return selectedRegion?.jurisdiction?.isTransitionalOrNewlyAdmitted ?? false;
  }, [selectedRegion?.jurisdiction?.isTransitionalOrNewlyAdmitted]);

  useEffect(() => {
    if (commitSpecialStatusOnFormSubmit) return;

    if (statuses && transitionOptions?.length) {
      if (!specialStatusChanged) return;

      const flag = transitionOptions.find(a => {
        return [...(a.activatesFlags ?? []), ...(a.removesFlags ?? [])]?.some(
          a => a.toLowerCase().indexOf('period') < 0
        );
      });
      const nextRuleSetFlag = flag?.activatesFlags;

      setCompliancePeriod(prev => {
        if (JSON.stringify(prev?.ruleSetFlag ?? []) === JSON.stringify(nextRuleSetFlag ?? [])) {
          return prev;
        }

        return {
          ...prev,
          ruleSetFlag: nextRuleSetFlag
        };
      });
    }
  }, [
    allSpecialStatuses,
    commitSpecialStatusOnFormSubmit,
    specialStatusChanged,
    statuses,
    transitionOptions
  ]);

  useEffect(() => {
    if (!complianceFormFields) return;

    setCompliancePeriod(prev => {
      const isEmptyState = JSON.stringify(prev) === JSON.stringify(emptyComplianceFormFields);
      if (!isEmptyState) return prev;

      return JSON.stringify(prev) === JSON.stringify(complianceFormFields)
        ? prev
        : complianceFormFields;
    });
  }, [complianceFormFields, emptyComplianceFormFields]);

  const previous = usePrevious(compliancePeriod);

  useEffect(() => {
    if (
      previous &&
      compliancePeriod &&
      JSON.stringify(previous) !== JSON.stringify(emptyComplianceFormFields) &&
      JSON.stringify(compliancePeriod) !== JSON.stringify(emptyComplianceFormFields) &&
      (compareDates(previous?.startDate ?? '', compliancePeriod?.startDate ?? '') !== 0 ||
        compareDates(previous?.endDate ?? '', compliancePeriod?.endDate ?? '') !== 0)
    ) {
      const isUserSet =
        compareDates(
          compliancePeriod?.startDate ?? '',
          compliancePeriod?.defaultStartDate ?? ''
        ) !== 0 ||
        compareDates(compliancePeriod?.endDate ?? '', compliancePeriod?.defaultEndDate ?? '') !== 0;

      setCompliancePeriod(prev => {
        if (prev?.isUserSet === isUserSet) return prev;

        return {
          ...prev,
          isUserSet: isUserSet
        };
      });
    }
  }, [compliancePeriod, emptyComplianceFormFields, previous]);

  const selectedCompliancePeriod = useMemo(
    () => getActiveCompliancePeriodValue(selectedRegion),
    [selectedRegion]
  );

  const { handleValidation, fieldsWithErrors, setFieldsWithErrors } = useComplianceFormValidation({
    compliancePeriod,
    reportingPeriod,
    selectedCompliancePeriod
  });

  const isUserSet = useMemo(() => {
    return compliancePeriod?.isUserSet;
  }, [compliancePeriod?.isUserSet]);

  const updateSpecialStatus = useCallback(async () => {
    if (specialStatusChanged) {
      setIsUpdatingForm(true);
      setIsUpdatingDateRange(true);

      const flag = selectedRegion?.jurisdiction?.cleStateTransitionOptions?.find(a => {
        return ![...(a.activatesFlags ?? []), ...(a.removesFlags ?? [])]?.some(
          a => a.toLowerCase().indexOf('period') >= 0
        );
      });
      const currentPeriod: CLEPeriod = {
        ...selectedRegion?.jurisdiction?.clePeriod,
        ruleSetFlag: flag?.activatesFlags
      };
      try {
        await switchStatusLazy({
          customerId: selectedRegion?.customerId,
          jurisdictionId: selectedRegion?.creditRegion_SK,
          jurisdictionKey: selectedRegion?.jurisdiction?.key,
          period: currentPeriod
        } as CreditTrackerUpdateRequest).unwrap();

        const response = await getComplianceStartEndDate([
          { ...lastTrackerRequest, individualSk: activelyViewingUserSk }
        ] as UserTrackerDataRequest[]);

        const foundTrackerData = findInRecordCollection(
          response.data?.trackerUsers ?? [],
          (x: UserTrackerData) =>
            x.creditRegionShortDescription == selectedRegion?.creditRegionShortDescription
        );

        foundTrackerData && setSelectedRegion({ ...foundTrackerData });

        return foundTrackerData;
      } catch (error) {
        showFailMessage('There was an error updating compliance period');
        return false;
      } finally {
        setIsUpdatingForm(false);
        setIsUpdatingDateRange(false);
      }
    }
  }, [
    specialStatusChanged,
    selectedRegion?.jurisdiction?.cleStateTransitionOptions,
    selectedRegion?.jurisdiction?.clePeriod,
    selectedRegion?.jurisdiction?.key,
    selectedRegion?.customerId,
    selectedRegion?.creditRegion_SK,
    selectedRegion?.creditRegionShortDescription,
    switchStatusLazy,
    getComplianceStartEndDate,
    lastTrackerRequest,
    activelyViewingUserSk,
    showFailMessage
  ]);

  const saveCompliancePeriod = useCallback(
    async (
      period?: ICLEPeriod,
      complianceGroupSK?: number,
      complianceGroupDescription?: string | null,
      ignoreSuccessMessage?: boolean,
      suppressCacheInvalidation?: boolean
    ) => {
      setIsLoading?.(true);

      const currentPeriod: CLEPeriod = {
        ...selectedClePeriod,
        ...(period ?? {}),
        isUserSet: period?.isUserSet ?? isUserSet,
        ruleSetFlag: commitSpecialStatusOnFormSubmit
          ? (period?.ruleSetFlag ?? selectedClePeriod?.ruleSetFlag)
          : (selectedClePeriod?.ruleSetFlag ?? period?.ruleSetFlag)
      };

      try {
        const updateOptions = suppressCacheInvalidation
          ? updateComplianceDateRange
          : updateUserOptions;
        await updateOptions({
          customerId: selectedRegion?.customerId,
          jurisdictionId: selectedRegion?.creditRegion_SK,
          jurisdictionKey: selectedRegion?.jurisdiction?.key,
          complianceGroupDescription: complianceGroupDescription,
          complianceGroupSk: complianceGroupSK,
          period: currentPeriod,
          reportingPeriodType: reportingPeriod
        } as CreditTrackerUpdateRequest)
          .unwrap()
          .then(() => !ignoreSuccessMessage && showSuccessMessage('Compliance period updated'));
        return true;
      } catch (error) {
        showFailMessage('There was an error updating compliance period');
        return false;
      } finally {
        setIsLoading?.(false);
      }
    },
    [
      setIsLoading,
      selectedClePeriod,
      commitSpecialStatusOnFormSubmit,
      isUserSet,
      updateComplianceDateRange,
      updateUserOptions,
      selectedRegion?.customerId,
      selectedRegion?.creditRegion_SK,
      selectedRegion?.jurisdiction?.key,
      reportingPeriod,
      showSuccessMessage,
      showFailMessage
    ]
  );

  const updateDateInterval = useCallback(
    async (period?: ICLEPeriod, complianceGroup?: IComplianceGroup) => {
      const requestId = ++dateRangeRequestRef.current;
      const currentPeriod: CLEPeriod = {
        ...selectedClePeriod,
        ...(period ?? {}),
        ruleSetFlag: commitSpecialStatusOnFormSubmit
          ? (period?.ruleSetFlag ?? selectedClePeriod?.ruleSetFlag)
          : (selectedClePeriod?.ruleSetFlag ?? period?.ruleSetFlag)
      };
      try {
        const updatePeriod = await updateComplianceDateRange({
          customerId: selectedRegion?.customerId,
          jurisdictionId: selectedRegion?.creditRegion_SK,
          jurisdictionKey: selectedRegion?.jurisdiction?.key,
          complianceGroupDescription: complianceGroup?.shortDescription,
          complianceGroupSk: complianceGroup?.sk,
          period: currentPeriod,
          reportingPeriodType: reportingPeriod
        } as CreditTrackerUpdateRequest);
        if (updatePeriod.error) {
          return;
        }
        hasProvisionalPeriodUpdateRef.current = true;
        if (requestId !== dateRangeRequestRef.current) return;
        setFieldsWithErrors({});
        const trackerRequest = [
          {
            ...lastTrackerRequest,
            individualSk: activelyViewingUserSk,
            flags: currentPeriod.ruleSetFlag
              ? [
                  ...lastTrackerRequestNowDateFlags,
                  ...excludeNowDateFlags(currentPeriod.ruleSetFlag)
                ]
              : lastTrackerRequest?.flags
          }
        ] as UserTrackerDataRequest[];
        const response = await getComplianceStartEndDate(trackerRequest);
        if (requestId !== dateRangeRequestRef.current) return;
        const foundTrackerData = findInRecordCollection(
          response.data?.trackerUsers ?? [],
          (x: UserTrackerData) =>
            x.creditRegionShortDescription == selectedRegion?.creditRegionShortDescription
        );
        return foundTrackerData;
      } catch (error) {
        if (requestId === dateRangeRequestRef.current) {
          console.error('There was an error updating compliance date interval');
        }
      }
    },
    [
      selectedClePeriod,
      commitSpecialStatusOnFormSubmit,
      updateComplianceDateRange,
      selectedRegion?.customerId,
      selectedRegion?.creditRegion_SK,
      selectedRegion?.jurisdiction?.key,
      selectedRegion?.creditRegionShortDescription,
      reportingPeriod,
      getComplianceStartEndDate,
      lastTrackerRequest,
      lastTrackerRequestNowDateFlags,
      activelyViewingUserSk,
      setFieldsWithErrors
    ]
  );

  const trackDateRangeUpdate = useCallback((request: Promise<UserTrackerData | undefined>) => {
    pendingDateRangeUpdatesRef.current.add(request);
    setIsUpdatingDateRange(true);
    void request.finally(() => {
      pendingDateRangeUpdatesRef.current.delete(request);
      if (!pendingDateRangeUpdatesRef.current.size) setIsUpdatingDateRange(false);
    });
    return request;
  }, []);

  const handleResetOnClose = useCallback(async () => {
    if (isResettingRef.current) return false;
    isResettingRef.current = true;

    try {
      await Promise.all(pendingDateRangeUpdatesRef.current);

      let rollbackSucceeded = true;
      if (specialStatusChanged && !commitSpecialStatusOnFormSubmit) {
        rollbackSucceeded = (await updateSpecialStatus()) !== false;
      }

      if (hasProvisionalPeriodUpdateRef.current) {
        const periodRollbackSucceeded = await saveCompliancePeriod(
          {
            ...complianceFormFields,
            ruleSetFlag: complianceFormFields?.ruleSetFlag
          },
          complianceFormFields?.complianceGroup?.sk,
          complianceFormFields?.complianceGroup?.longDescription,
          true,
          true
        );
        hasProvisionalPeriodUpdateRef.current = !periodRollbackSucceeded;
        rollbackSucceeded = periodRollbackSucceeded && rollbackSucceeded;
      }

      return rollbackSucceeded;
    } finally {
      isResettingRef.current = false;
    }
  }, [
    complianceFormFields,
    commitSpecialStatusOnFormSubmit,
    saveCompliancePeriod,
    specialStatusChanged,
    updateSpecialStatus
  ]);

  /** @private */
  const updateStoredData = useCallback((tracker?: UserTrackerData) => {
    if (!tracker) return;

    const newComplianceGroup = tracker?.complianceGroups?.find(
      x => x.sk === tracker?.complianceGroup_SK
    );

    const selectedClePeriod = findSelectedClePeriod(tracker.jurisdiction?.clePeriod);
    const startDate = new Date(selectedClePeriod?.startDate ?? '');
    const endDate = new Date(selectedClePeriod?.endDate ?? '');
    setCompliancePeriod(prev => ({
      ...prev,
      complianceGroup: { ...newComplianceGroup },
      startDate: isValid(startDate) ? format(startDate, 'MM/dd/yyyy') : '',
      endDate: isValid(endDate) ? format(endDate, 'MM/dd/yyyy') : '',
      defaultStartDate: selectedClePeriod?.defaultStartDate,
      defaultEndDate: selectedClePeriod?.defaultEndDate,
      hasDefaultDate: selectedClePeriod?.hasDefaultDate
    }));
  }, []);

  const handleResetComplianceDates = useCallback(async () => {
    if (isResettingRef.current) return;

    const updatedDates = await trackDateRangeUpdate(
      updateDateInterval(
        {
          ...compliancePeriod,
          isUserSet: false,
          ruleSetFlag: [...new Set([...(selectedClePeriod?.ruleSetFlag ?? []), '!RESET'])]
        },
        compliancePeriod?.complianceGroup as IComplianceGroup
      )
    );
    updateStoredData(updatedDates);
  }, [
    compliancePeriod,
    selectedClePeriod?.ruleSetFlag,
    trackDateRangeUpdate,
    updateDateInterval,
    updateStoredData
  ]);

  const waitForReportingPeriodTypeRequired = useCallback(
    (key: ComplianceFieldNames, value: string | number | IComplianceGroup | undefined) => {
      if (!value) return true;

      switch (reportingPeriod) {
        /*Birthday + Admission date */
        case ReportingPeriodType._2: {
          if (
            key == ComplianceFieldNames.ADMIT_DATE &&
            !selectedRegion?.jurisdiction?.isTransitionalOrNewlyAdmitted
          ) {
            return !compliancePeriod?.birthDay || !compliancePeriod.birthMonth;
          }
          if (key == ComplianceFieldNames.BIRTH_DAY) {
            return !compliancePeriod?.admitDate || !compliancePeriod.birthMonth;
          }
          if (key == ComplianceFieldNames.BIRTH_MONTH) {
            return !compliancePeriod?.admitDate || !compliancePeriod.birthDay;
          }
          return false;
        }
        /*Birthday */
        case ReportingPeriodType._3: {
          if (key == ComplianceFieldNames.BIRTH_DAY) {
            return !compliancePeriod.birthMonth;
          }
          if (key == ComplianceFieldNames.BIRTH_MONTH) {
            return !compliancePeriod.birthDay;
          }
          return false;
        }
        /*Compliance Group + Admission Date */
        case ReportingPeriodType._5: {
          if (key == ComplianceFieldNames.COMPLIANCE_GROUP) {
            return !compliancePeriod?.admitDate;
          }
          if (key == ComplianceFieldNames.ADMIT_DATE) {
            return !compliancePeriod?.complianceGroup;
          }
          return false;
        }
      }
      return false;
    },
    [
      compliancePeriod?.admitDate,
      compliancePeriod.birthDay,
      compliancePeriod.birthMonth,
      compliancePeriod?.complianceGroup,
      reportingPeriod,
      selectedRegion?.jurisdiction?.isTransitionalOrNewlyAdmitted
    ]
  );

  const handleChange = useCallback(
    async (key: ComplianceFieldNames, value: string | number | IComplianceGroup | undefined) => {
      if (isResettingRef.current) return;

      const newPeriod: ComplianceFormFields = {
        ...compliancePeriod,
        [key]: value,
        isUserSet: key == ComplianceFieldNames.START_DATE || key == ComplianceFieldNames.END_DATE
      };

      setCompliancePeriod(newPeriod);

      if (
        key == ComplianceFieldNames.ADMIT_DATE &&
        (typeof value === 'string' || value instanceof String)
      ) {
        const date = new Date(value as string);
        if (value.length != 10 || date.getFullYear() < 1900) {
          return;
        }
      }

      if (
        key == ComplianceFieldNames.START_DATE ||
        key == ComplianceFieldNames?.END_DATE ||
        waitForReportingPeriodTypeRequired(key, value)
      )
        return;

      const updatedDates = await trackDateRangeUpdate(
        updateDateInterval(
          { ...newPeriod, [key]: value },
          newPeriod?.complianceGroup as IComplianceGroup
        )
      );
      updateStoredData(updatedDates);
    },
    [
      compliancePeriod,
      trackDateRangeUpdate,
      updateDateInterval,
      updateStoredData,
      waitForReportingPeriodTypeRequired
    ]
  );

  const handleSaveButton = useCallback(
    async (displaySuccessMessage: boolean = true) => {
      if (pendingDateRangeUpdatesRef.current.size) return false;

      setIsSubmitting(true);
      if (handleValidation(isTransitionalOrNewlyAdmittedRegion)) {
        if (commitSpecialStatusOnFormSubmit && pendingStatuses) {
          setStatuses(pendingStatuses);
          setPendingStatuses(undefined);
        }

        const saved = await saveCompliancePeriod(
          compliancePeriod,
          compliancePeriod.complianceGroup?.sk,
          compliancePeriod.complianceGroup?.longDescription,
          !displaySuccessMessage
        );
        setIsSubmitting(false);

        if (!saved) return false;

        hasProvisionalPeriodUpdateRef.current = false;
        setOpen(false);
        return true;
      }
      setIsSubmitting(false);
      return false;
    },
    [
      compliancePeriod,
      commitSpecialStatusOnFormSubmit,
      handleValidation,
      isTransitionalOrNewlyAdmittedRegion,
      pendingStatuses,
      saveCompliancePeriod,
      setOpen
    ]
  );

  const showReset = useMemo(() => {
    return (
      !isUsingDefaultDates(compliancePeriod) &&
      !isMissingCompliancePeriod &&
      !!selectedRegion?.jurisdiction?.jurisdictionReportingPeriod &&
      !!compliancePeriod?.isUserSet
    );
  }, [
    compliancePeriod,
    isMissingCompliancePeriod,
    selectedRegion?.jurisdiction?.jurisdictionReportingPeriod
  ]);

  const handleSelectorChange = useCallback(
    async (status: SpecialStatusSelector) => {
      setShowSpecialStatusError(false);
      if (selectedStatuses.some(a => a.text === status.text && a.value?.toLowerCase() === 'true')) {
        return;
      }

      const nextStatuses = selectedStatuses.map(a => ({
        ...a,
        value: `${a.text === status.text}`
      }));

      if (commitSpecialStatusOnFormSubmit) {
        setPendingStatuses(nextStatuses);
        return;
      } else {
        setStatuses(nextStatuses);
      }

      if (onSpecialStatusChange) {
        const requestId = ++specialStatusChangeRequestRef.current;
        setIsUpdatingForm(true);
        try {
          const nextCompliancePeriod = await onSpecialStatusChange(nextStatuses);
          if (nextCompliancePeriod && requestId === specialStatusChangeRequestRef.current) {
            setCompliancePeriod({
              ...emptyComplianceFormFields,
              ...nextCompliancePeriod
            });
          }
        } finally {
          if (requestId === specialStatusChangeRequestRef.current) {
            setIsUpdatingForm(false);
          }
        }
      }
    },
    [
      commitSpecialStatusOnFormSubmit,
      emptyComplianceFormFields,
      onSpecialStatusChange,
      selectedStatuses,
      setStatuses
    ]
  );

  const handleSpecialStatusValidation = useCallback(async () => {
    const anySelected = selectedStatuses.some(
      status => status.value.toString()?.toLowerCase() === 'true'
    );
    setShowSpecialStatusError(!anySelected);

    if (anySelected) {
      if (commitSpecialStatusOnFormSubmit) {
        if (onSpecialStatusChange) {
          setIsUpdatingForm(true);
          try {
            const nextCompliancePeriod = await onSpecialStatusChange(selectedStatuses);
            if (nextCompliancePeriod) {
              setCompliancePeriod({
                ...emptyComplianceFormFields,
                ...nextCompliancePeriod
              });
            }
          } finally {
            setIsUpdatingForm(false);
          }
        }
        setStepIndex(stepIndex + 1);
        return true;
      }

      const foundTrackerData = await updateSpecialStatus();

      if (foundTrackerData) {
        const foundSelectedPeriod = findSelectedClePeriod(
          foundTrackerData?.jurisdiction?.clePeriod
        );

        if (
          !periodIsMissingComplianceInformation(
            foundSelectedPeriod,
            foundTrackerData?.jurisdiction?.requireSetup
          )
        ) {
          const periodAdmitDate = getPeriodAdmitDate(foundSelectedPeriod ?? {}),
            periodStartDate = getPeriodStartDate(foundSelectedPeriod ?? {}),
            periodEndDate = getPeriodEndDate(foundSelectedPeriod ?? {}),
            birthDate = getUserBirthDate(
              foundTrackerData?.jurisdiction?.userComplianceInformation?.birthDate ?? ''
            );

          setCompliancePeriod({
            ...foundSelectedPeriod,
            complianceGroup: foundTrackerData?.complianceGroups?.find(
              x => x.sk === foundTrackerData?.complianceGroup_SK
            ),
            birthMonth: (birthDate?.getMonth() ?? 0) + 1,
            birthDay: birthDate?.getDate() ?? 0,
            admitDate: periodAdmitDate ? format(periodAdmitDate, 'MM/dd/yyyy') : undefined,
            startDate: periodStartDate ? format(periodStartDate, 'MM/dd/yyyy') : '',
            endDate: periodEndDate ? format(periodEndDate, 'MM/dd/yyyy') : ''
          });
        } else {
          setCompliancePeriod({
            ...emptyComplianceFormFields,
            ruleSetFlag: foundTrackerData.jurisdiction?.clePeriod?.ruleSetFlag ?? []
          });
        }
      }
      setStepIndex(stepIndex + 1);
    }
    return anySelected;
  }, [
    commitSpecialStatusOnFormSubmit,
    emptyComplianceFormFields,
    onSpecialStatusChange,
    selectedStatuses,
    stepIndex,
    updateSpecialStatus
  ]);

  return {
    setCompliancePeriod,
    compliancePeriod,
    handleChange,
    handleResetOnClose,
    handleSaveButton,
    handleValidation,
    handleResetComplianceDates,
    fieldsWithErrors,
    setFieldsWithErrors,
    reportingPeriod,
    isSubmitting,
    isTransitionalOrNewlyAdmittedRegion,
    isUpdatingDateRange,
    isUpdatingForm,
    showReset,
    selectedRegion,
    stepIndex,
    setStepIndex,
    showSuccessMessage,
    showFailMessage,
    specialStatus: {
      statuses: selectedStatuses,
      showSpecialStatusError,
      setStatuses,
      updateSpecialStatus,
      handleSelectorChange,
      handleSpecialStatusValidation
    }
  };
};
export default useComplianceModal;
